/**
 * Account → AI agent access (migration 070). Action first (owner feedback
 * 2026-09-27): the one-paste command with a primary "Copy command" button,
 * two short lines on what the agent does and what needs a person, one
 * "Other ways to connect" disclosure, then Connected agents — agent changes
 * waiting for a person (since 096 only an invitation, or an older change
 * still in its 074 window) and the sign-ins and tokens, revocable. When a
 * change waits, a callout at the top says so. What agents changed, with Undo,
 * is at the top of the dashboard (agent/AgentChangesBanner.tsx).
 * Owners and admins see every token in the organisation; a developer sees the
 * ones they created. Each token links to its activity in the audit log (T093).
 */
import { useRef } from 'react';
import type { AgentToken } from '../api/types';
import { can } from '../auth/roles';
import { useSession } from '../auth/session';
import { AgentHandoff } from '../agent/AgentHandoff';
import { DOCS_PATH } from '../agent/handoff';
import { PendingList, usePendingChanges } from '../agent/PendingApprovals';
import { agentActivityPath } from './Audit';
import { Link } from '../router';
import { useSites } from '../state/sites';
import { ErrorNotice, Loading, PageHead, Time, useToast } from '../ui/kit';
import { useMutation, useQuery } from '../ui/hooks';

function hostOf(uri: string): string {
  try {
    return new URL(uri).host;
  } catch {
    return uri;
  }
}

function statusTag(t: AgentToken) {
  if (t.status === 'active') return <span className="tag ok">Active</span>;
  if (t.status === 'revoked') return <span className="tag">Revoked</span>;
  return <span className="tag warn">Expired</span>;
}

export function AgentAccess() {
  const { api, role } = useSession();
  const canApprove = can(role, 'approveChanges');
  const approvals = usePendingChanges(canApprove);
  const approvalsHead = useRef<HTMLHeadingElement>(null);
  const { pairs } = useSites();
  const toast = useToast();
  const tokens = useQuery(() => api.listAgentTokens(), []);
  const revoke = useMutation(async (id: string) => {
    await api.revokeAgentToken(id);
    toast('Token revoked. The agent loses access on its next request.');
    void tokens.reload();
    return true;
  });
  // The per-token activity view is the audit log filtered to this token
  // (T093); the audit log is owner/admin, so a developer sees no link.
  const canAudit = can(role, 'audit');
  const siteLabel = (key: string) => pairs.find((p) => p.live.site_key === key)?.live.label ?? key;
  const items = tokens.data?.items ?? [];
  const active = items.filter((t) => t.status === 'active');
  const past = items.filter((t) => t.status !== 'active').slice(0, 10);

  const row = (t: AgentToken) => (
    <li key={t.id} className="row-between token-row" style={{ alignItems: 'flex-start', padding: 'var(--s3) 0', borderBottom: '1px solid var(--line)' }}>
      <div className="stack-sm grow" style={{ gap: 2 }}>
        <div className="row" style={{ gap: 8 }}>
          <strong style={{ color: 'var(--ink)' }}>{t.label}</strong>
          {statusTag(t)}
          <span className="tag">{t.scopes.includes('config') ? 'Set up' : 'Read only'}</span>
          {t.kind === 'oauth' ? <span className="tag">Browser sign-in</span> : null}
        </div>
        <span className="small muted">
          {t.kind === 'oauth' ? (
            <>signed in from {t.client?.id ? hostOf(t.client.id) : 'an MCP client'}</>
          ) : (
            <code>{t.token_prefix}</code>
          )}{' '}
          · {t.site_keys ? t.site_keys.map(siteLabel).join(', ') : 'All sites'} · {t.kind === 'oauth' ? 'approved' : 'created'} by{' '}
          {t.created_by_email ?? 'unknown'}{' '}
          <Time iso={t.created_at} />
        </span>
        <span className="small muted">
          {t.status === 'revoked' ? (
            <>Revoked <Time iso={t.revoked_at} /></>
          ) : (
            <>Expires <Time iso={t.expires_at} /></>
          )}{' '}
          · {t.last_used_at ? <>last used <Time iso={t.last_used_at} /></> : 'never used'}
          {canAudit ? (
            <>
              {' '}
              · <Link to={agentActivityPath(t.id)} aria-label={`Activity of “${t.label}” in the audit log`}>Activity</Link>
            </>
          ) : null}
        </span>
      </div>
      {t.status === 'active' ? (
        <button
          type="button"
          className="btn btn-sm btn-danger"
          disabled={revoke.pending}
          onClick={() => {
            if (window.confirm(`Revoke “${t.label}”? Any agent using it stops working immediately.`)) void revoke.run(t.id);
          }}
        >
          Revoke
        </button>
      ) : null}
    </li>
  );

  const waiting = canApprove ? approvals.pending.length : 0;
  const nothing = !tokens.loading && !tokens.error && items.length === 0 && waiting === 0;

  return (
    <div className="stack-lg">
      <PageHead
        title="AI agent access"
        lede={
          <>
            Let Claude Code set up FinderlyFix in your website’s code. A few security steps stay with you. Anything that sends homeowner data somewhere new takes effect at once, and owners and admins are emailed right away with a one-click Undo.{' '}
            <Link to={DOCS_PATH}>How it works</Link>
          </>
        }
      />
      {waiting ? (
        <div className="notice warn approval-callout">
          <strong>
            {waiting === 1 ? '1 agent change' : `${waiting} agent changes`} waiting: review or reject
          </strong>
          <button
            type="button"
            className="btn btn-primary btn-sm"
            onClick={() => {
              approvalsHead.current?.scrollIntoView({ block: 'start', behavior: 'smooth' });
              approvalsHead.current?.focus({ preventScroll: true });
            }}
          >
            Review
          </button>
        </div>
      ) : null}
      <AgentHandoff onCreated={() => void tokens.reload()} />
      <section className="stack" aria-labelledby="connected-title">
        <div className="row" style={{ gap: 8 }}>
          <h2 id="connected-title">Connected agents</h2>
          {active.length ? <span className="tag">{active.length} active</span> : null}
        </div>
        {canApprove ? (
          <PendingList
            list={approvals}
            frame={(list) => (
              <section className="panel" aria-labelledby="approvals-title">
                <div className="panel-head">
                  <h3 id="approvals-title" ref={approvalsHead} tabIndex={-1}>
                    AI agent changes waiting
                  </h3>
                  <span className="tag warn">{waiting} pending</span>
                </div>
                <div className="panel-body">{list}</div>
              </section>
            )}
          />
        ) : null}
        <ErrorNotice error={tokens.error ?? revoke.error} />
        {tokens.loading ? (
          <Loading />
        ) : nothing ? (
          <p className="muted">No agents connected yet.</p>
        ) : items.length ? (
          <div className="panel">
            <div className="agent-tokens">
              {active.length ? (
                <ul style={{ listStyle: 'none', margin: 0, padding: 0 }}>{active.map(row)}</ul>
              ) : (
                <p className="muted small" style={{ margin: 'var(--s3) 0' }}>No active agents.</p>
              )}
              {past.length ? (
                <details className="help" style={{ margin: 'var(--s3) 0' }}>
                  <summary>Expired and revoked ({past.length})</summary>
                  <ul style={{ listStyle: 'none', margin: 0, padding: 0 }}>{past.map(row)}</ul>
                </details>
              ) : null}
            </div>
          </div>
        ) : null}
      </section>
    </div>
  );
}
